import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {Copybox, CustomComponent, Link, Page, ShareIcon, Tooltip} from '../../common/components';
import {FlagIcon} from '../../common/components/FlagIcon';
import {TrashIcon} from '../../common/components/TrashIcon';
import {DetectContextMenuWrapper} from '../../common/components/DetectContextMenuWrapper';
import {Icon} from '../../common/components/Icon';
import {Metadata} from '../../common/metadata';
import {capitalize, cleanMultiwordTag, getArticleLDJSON, gifCountryAllowed, isMobile, parseViewId, parseViewShortId, safelySerializeJSON, tryDecodeURI, window, isSticker, isStaticImage} from '../../common/util';

import {Gif, MEDIA_QUALITY_MAPPING, MEDIA_QUALITY} from '../components/Gif';
import {MEDIA_TYPE, getMediaType} from '../components/GifConstants';
import {GifFavButton} from '../components/GifFavButton';
import {GifRestrictedMessage} from '../components/GifRestrictedMessage';
import {UniversalGifList} from '../components/UniversalGifList';
import {gifOGMeta} from '../components/gifOGMeta';
import {ProfileImage} from '../components/ProfileImage';
import {ShareCount} from '../components/ShareCount';
import {RelatedTag} from '../components/Tag';
import {UpsellPill} from '../components/UpsellPill';
import {OfficialBadge} from '../components/OfficialBadge';

import {subscribe, transformProps} from '../../../replete';


function addShareButtonUtm(baseUrl: string, type: string) {
    const trackingSearchParams = new URLSearchParams();
    trackingSearchParams.set('utm_source', 'share-button');
    trackingSearchParams.set('utm_medium', 'Social');
    trackingSearchParams.set('utm_content', type);
    return `${baseUrl}?${trackingSearchParams.toString()}`;
}

function QualityToggle(props: jsx.Props, context: jsx.AppGlobals) {
    const gettextSub = context.gtService.gettextSub;

    const {gifQuality, gifPage, gif, gifLoaded} = props;
    const selected = gifLoaded && gifQuality;
    const mediaType = getMediaType(gif);

    const formatMissing = (quality: string) => {
        let formats = MEDIA_QUALITY_MAPPING[quality][mediaType];
        if (!Array.isArray(formats)) {
            formats = [formats];
        }
        return !formats.some((format: string) => Gif.hasFormat(gif, format));
    };

    const clickHandler = (gifQuality: string) => {
        window.gaEvent({
            category: 'QualityToggle',
            action: 'click',
            label: MEDIA_QUALITY_MAPPING[gifQuality][mediaType],
        });
        gifPage.setSize(gifQuality);
    };

    return <div className="QualityToggle">
        <button
            className={selected === MEDIA_QUALITY.SD ? 'selected' : ''}
            disabled={!gifLoaded || formatMissing(MEDIA_QUALITY.SD)}
            onClick={() => clickHandler(MEDIA_QUALITY.SD)}
        > ● {gettextSub('SD GIF')} </button>

        <button
            className={selected === MEDIA_QUALITY.HD ? 'selected' : ''}
            disabled={!gifLoaded || formatMissing(MEDIA_QUALITY.HD)}
            onClick={() => clickHandler(MEDIA_QUALITY.HD)}
        > ● {gettextSub('HD GIF')} </button>

        { mediaType === MEDIA_TYPE.GIF && <button
            className={selected === MEDIA_QUALITY.VIDEO ? 'selected' : ''}
            disabled={!gifLoaded || formatMissing(MEDIA_QUALITY.VIDEO)}
            onClick={() => clickHandler(MEDIA_QUALITY.VIDEO)}
        > ● {gettextSub('MP4')} </button> }
    </div>;
}

class ShareCountFooter extends CustomComponent {
    activateTooltip?: Function;
    deactivateTooltip?: Function;

    @autobind
    addTooltipActivationCallback(callback: Function) {
        this.activateTooltip = callback;
    }
    @autobind
    addTooltipDeativationCallback(callback: Function) {
        this.deactivateTooltip = callback;
    }
    render() {
        const gettextSub = this.context.gtService.gettextSub;
        return <div className="ShareCountFooter" style={{'width': `${70 * this.props.width / this.props.height}vh`, 'max-width': `${this.props.width}px`}}>
            <ShareCount gif={this.props.gif}/>
            <div className="icon">
                <Icon name="info-outline-icon" onClick={() => {
                    this.activateTooltip?.();
                }}/>
                <Tooltip
                    hasCallout={true}
                    addTooltipActivationCallback={
                        this.addTooltipActivationCallback}
                    addTooltipDeactivationCallback={
                        this.addTooltipDeativationCallback}
                >
                    <Icon name="close-icon" onClick={() => {
                        this.context.request.console.debug('calling deactivateTooltip', this.deactivateTooltip);
                        this.deactivateTooltip?.();
                    }}/>
                    <h2>{gettextSub('Shares')}</h2>
                    <p>{gettextSub('This number reflects how many times your asset has been copied or shared to other platforms.')}</p>
                </Tooltip>
            </div>
        </div>;
    }
}


@transformProps((props: jsx.Props, context: jsx.AppGlobals) => {
    props.anonid = context.apiService.getAnonid();
    return props;
})
@subscribe({
    appConfig: ['api.appConfig.*', 'anonid'],
})
class CaptionButton extends Component<jsx.Props, jsx.State> {
    render(props: jsx.Props) {
        const gettextSub = this.context.gtService.gettextSub;
        const localizeUrlPath = this.context.gtService.localizeUrlPath;

        const {anonid, appConfig, gif} = props;

        let captionIdParam = '';
        let captionUrl = '';
        if (appConfig.flags.web_waffles_captioning_enabled) {
            captionIdParam = gif?.id ? `pid=${gif.id}&` : '';
            captionUrl = `https://create.tenor.com/#${captionIdParam}anonid=${anonid}&exp_ids=${this.context.apiService.getActiveExpIds()}`;
        } else {
            captionIdParam = gif?.id ? `caption_item_id=${gif.id}&` : '';
            captionUrl = localizeUrlPath(
                // eslint-disable-next-line max-len
                `/gif-maker?${captionIdParam}utm_source=gif-caption&utm_medium=internal&utm_campaign=gif-maker-entrypoints`,
            );
        }

        return <Link
            to={captionUrl}
            className="caption-gif-button"
            clickLabel="caption-gif-button"
            onClick={() => {
                this.context.apiService.registerEvent(
                    'item_view_caption_tap',
                    {
                        rid: gif.id,
                    },
                );
            }}
        >
            {gettextSub('CAPTION')}
        </Link>;
    }
}

function RelatedGifsContainer({currentGifId, relatedGifs}, context) {
    const gettextSub = context.gtService.gettextSub;
    relatedGifs = relatedGifs.results.filter((
        g: api.Post) => {
        return !currentGifId || g.id !== currentGifId;
    }).slice(0, 7);
    return <div className="RelatedGifsContainer">
        <h3>{gettextSub('Related GIFs')}</h3>
        <UniversalGifList
            gifs={relatedGifs}
            staticColumns={!isMobile() ? 1 : undefined}
        />
    </div>;
}


@transformProps((props: jsx.Props, context: jsx.AppGlobals) => {
    const routeId = tryDecodeURI(props.match.params.id || '');
    const routeShortId = tryDecodeURI(props.match.params.shortId || '');

    if (routeId) {
        props.gifId = parseViewId(routeId);
    } else {
        props.gifId = parseViewShortId(routeShortId);
    }
    props.routeId = routeId;

    return props;
})
@subscribe({
    auth: ['api.auth'],
    gif: ['api.gifs.byId.*', 'gifId'],
    relatedGifs: ['api.gifs.related.*', 'gifId'],
})
export class GifPage extends Page {
    gifContainer: HTMLElement|null;
    minStickerDim: number;
    embedUrl?: string;
    isSticker?: boolean;
    isStaticImage?: boolean;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.gifContainer = null;
        this.minStickerDim = 256;
        this.state = {
            ...this.state,
            gifLoaded: false,
            showCopyBox: false,
            gifQuality: MEDIA_QUALITY.SD,
            muted: true,
            minHeight: '',
            width: 0,
            height: 0,
        };
        this.fixHeight();
    }
    pageChanged(nextProps: jsx.Props) {
        return nextProps.gifId !== this.props.gifId ||
            nextProps.gif !== this.props.gif;
    }
    propsNeedProcessing(nextProps: jsx.Props) {
        for (const key of ['gifs', 'relatedGifs']) {
            if (nextProps[key] !== this.props[key]) {
                return true;
            }
        }
        return false;
    }
    pageInit(props: jsx.Props) {
        this.debug('pageInit', props.id, props.shortId);

        this.ttl = this.context.CONSTANTS.VIEW_PAGE_TTL;
        this.setState({
            gifLoaded: false,
            showCopyBox: false,
            gifQuality: MEDIA_QUALITY.SD,
            muted: true,
        });

        this.fixHeight(props);
    }
    @autobind
    handlePageDone() {
        super.handlePageDone();
        this.context.request.console.log('handlePageDone2');
        if (process.env.BROWSER) {
            this.context.apiService.registerEvent('itemview_impression', {
                rid: this.props.gifId,
            });
        }
        if (process.env.BROWSER && this.props.match.params.action === 'add-favorite') {
            if (this.context.authService.isLoggedIn()) {
                this.context.apiService.setFavorite({
                    pid: this.props.gifId,
                    remove: false,
                });
            } else {
                this.context.authService.showLoginDialog();
            }
        }
    }
    @autobind
    toggleMute(event: Event) {
        this.setState({muted: !this.state.muted});

        // Prevent context diallog from opening.
        event.stopPropagation();
    }
    @autobind
    onShareIconClick(event: Event, socialIcon: any) {
        this.registerShare();
        if (['link', 'embed'].includes(socialIcon.props.service)) {
            this.setState({showCopyBox: true});
        }
        window.gaEvent({
            category: 'Share Button',
            action: 'click',
            label: socialIcon.props.service,
        });
    }

    @autobind
    registerShare() {
        const gif = this.props.gif.results[0];
        this.context.apiService.registerShare(gif);

        if (gif.feature_info) {
            this.context.apiService.registerAdShare(gif);
        }
    }

    @autobind
    fixHeight(props: jsx.Props = this.props) {
        this.context.request.console.debug('fixHeight');
        // width: 100% and height: auto causes the element to be collapsed
        // during load, so no bg_color shows up. So we have to manually set
        // this in JS.
        // Element width not available on backend.

        // NOTE page resize handler passes event object and we must use
        // this.props instead.
        const gif = props.gif ?
            props.gif.results[0] : this.props.gif.results[0];
        const newState: jsx.State = {};
        const containerWidth = this.gifContainer?.offsetWidth || 400;
        let aspectRatio;

        if (gif && this.state) {
            const {gifQuality} = this.state!;
            const mediaType = getMediaType(gif);
            const format = MEDIA_QUALITY_MAPPING[gifQuality][mediaType];
            const dims = Gif.getFormatDims(gif, format) || [100, 100];
            aspectRatio = dims[0] / dims[1];
            newState.minHeight = '';

            if (mediaType === MEDIA_TYPE.STICKER) {
                const maxDimension = (dims[0] > dims[1] ? dims[0] : dims[1]);
                const minWidth = this.minStickerDim +
                    Gif.getStickerPadding() * 2;
                let width = maxDimension + Gif.getStickerPadding() * 2;
                width = width < minWidth ? minWidth : width; // ensure minimum width
                width = width > containerWidth ? containerWidth : width; // ensure width remains bounded by container
                newState.width = width;
                newState.height = width;
            } else {
                newState.height = containerWidth / aspectRatio;
                newState.width = newState.height * aspectRatio;
            }
        } else {
            aspectRatio = 1.33;
            newState.height = containerWidth / aspectRatio;
            newState.width = newState.height * aspectRatio;
            newState.minHeight = `${newState.height}px`;
        }
        this.setState(newState);
    }
    componentDidMount() {
        // needed so that Gif size gets updated in render().
        window.addEventListener('resize', this.fixHeight);
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.fixHeight);
    }
    @autobind
    setGifContainer(element: HTMLElement|null) {
        this.gifContainer = element;
        this.fixHeight();
    }
    processNewProps(props: jsx.Props) {
        const gettextSub = this.context.gtService.gettextSub;

        const body = props.gif;
        this.context.request.console.debug('props.gifId', props.gifId);
        if (props.gifId === '' || props.gifId === null) {
            this.context.response.status = 404;
            return;
        }
        if (body.results.length === 0) {
            if (!body.pending) {
                this.context.response.status = 404;
            }
            return;
        }
        this.context.response.status = 200;
        this.context.response.vary('x-preferred-type');

        const gif = body.results[0];

        this.isSticker = isSticker(gif);
        this.isStaticImage = isStaticImage(gif);

        const mediaType = getMediaType(gif);

        const hdMediaFormat = MEDIA_QUALITY_MAPPING[
            MEDIA_QUALITY.HD][mediaType];
        const sdMediaFormat = MEDIA_QUALITY_MAPPING[
            MEDIA_QUALITY.SD][mediaType];
        const fallbackFormat = MEDIA_QUALITY_MAPPING[
            MEDIA_QUALITY.FALLBACK][mediaType];

        if (Gif.hasFormat(gif, hdMediaFormat) &&
            Gif.getFormatParam<'size'>(
                gif, hdMediaFormat, 'size') < 4000000) {
            this.setState({gifQuality: MEDIA_QUALITY.HD});
        } else if (Gif.hasFormat(gif, sdMediaFormat)) {
            this.setState({gifQuality: MEDIA_QUALITY.SD});
        } else if (Gif.hasFormat(gif, fallbackFormat)) {
            this.setState({gifQuality: MEDIA_QUALITY.FALLBACK});
        } else if (!body.pending && !props.relatedGifs.pending) {
            this.context.response.status = 404;
            return;
        }

        const gifExt = props.routeId.substr(-4) === '.gif';
        // Forward to GIF URL if .gif was requested or a bot asks for
        // something besides html.
        if (gifExt || this.context.request.preferredType === 'img-redirect') {
            this.redirect(Gif.getFormatUrlAuthed(gif, 'gif', this.context.apiService));
            return;
        } else {
            this.setCanonicalURL(this.linkToView(gif));
        }

        this.fixHeight(props);

        const first3Terms = gif.tags.slice(0, 3).map(capitalize).join(' ');

        this.h1_title = gif.h1_title;
        this.title = gettextSub('{h1_title} - {first3Terms} - Discover & Share GIFs', {h1_title: this.h1_title, first3Terms: first3Terms});

        this.description = gettextSub('The perfect {first3Terms} Animated GIF for your conversation. Discover and Share the best GIFs on Tenor.', {first3Terms});
        this.keywords = `${gif.tags.join(',')},gif,animated gif,gifs,meme`;

        this.embedUrl = this.fullURL(`/embed/${gif.id}`);

        window.fbq('track', 'ViewContent', {content_name: this.title});
    }
    getShareButtons(gif: api.Post) {
        const shareTags = gif.tags.slice(0, 3);
        return {
            'imessage': {},
            'facebook': {
                'url': addShareButtonUtm(this.canonicalURL, 'facebook'),
            },
            'twitter': {
                'url': addShareButtonUtm(this.canonicalURL, 'twitter'),
                'tags': shareTags,
            },
            'reddit': {
                'url': addShareButtonUtm(this.canonicalURL, 'reddit'),
            },
            'pinterest': {
                'url': addShareButtonUtm(this.canonicalURL, 'pinterest'),
                'mediaUrl': Gif.getFormatUrl(gif, 'gif'),
            },
            'tumblr': {
                'url': addShareButtonUtm(this.canonicalURL, 'tumblr'),
                'tags': shareTags,
            },
            'link': {
                copyText: this.fullURL(new URL(gif.url).pathname),
            },
            'embed': {
                copyText: gif.embed,
            },
        };
    }

    @autobind
    onCompletedGif() {
        this.context.request.console.debug('onCompletedGif');
        if (this.state && !this.state!.gifLoaded) {
            this.setState({gifLoaded: true});
        }
    }
    @autobind
    setSize(size: string) {
        this.setState({gifQuality: size});
    }
    @autobind
    onGifContainerContextMenu() {
        const gif = this.props.gif.results[0];
        if (gif?.media_formats) {
            window.gaEvent({
                category: 'single-gif-container',
                action: 'contextmenu',
            });
        }
    }
    @autobind
    profileLinkingDisabled(gif: api.Post) {
        const denylist = this.context.CONSTANTS.PROFILE_LINK_DENYLIST;
        return gif.user && denylist.includes(gif.user.username);
    }

    renderPage() { // eslint-disable-line complexity
        const gettextSub = this.context.gtService.gettextSub;

        const state = this.state!;
        const gif = this.props.gif.results[0];
        const detailsEls = [];
        let mp4Format;
        let gifFormat;
        let gifAspectRatio = 1;
        let iosURL;
        let androidURL;

        let articleLDJSON: any = {};
        let publisherLDJSON: any = {};
        let gifLDJSON: any = {};
        let videoLDJSON: any = {};

        if (gif) {
            iosURL = `riffsykeyboard://riff/${gif.id}`;
            androidURL = `riffsy://gifs/${gif.id}`;
            mp4Format = Gif.getFormat(gif, 'mp4');
            gifFormat = Gif.getFormat(gif, 'gif');
            gifAspectRatio = gifFormat.dims[0] / gifFormat.dims[1];

            if (gif.content_description !== gif.h1_title) {
                detailsEls.push(<dd>{gettextSub('Content Description')}:&nbsp;{gif.content_description}</dd>);
            }
            if (gif.generatedcaption) {
                detailsEls.push(<dd>{gettextSub('Caption')}:&nbsp;&ldquo;{gif.generatedcaption}&rdquo;</dd>);
            }
            if (gifFormat.size) {
                detailsEls.push(<dd>{gettextSub('File Size')}:&nbsp;{Math.ceil(gifFormat.size / 1024)}KB</dd>);
            }
            if (mp4Format.duration) {
                detailsEls.push(<dd>{gettextSub('Duration')}:&nbsp;{mp4Format.duration.toFixed(3)} sec</dd>);
            }
            detailsEls.push(<dd>{gettextSub('Dimensions')}:&nbsp;{gifFormat.dims[0]}x{gifFormat.dims[1]}</dd>);
            if (gif.created) {
                const createdDT = new Date(gif.created * 1000);
                detailsEls.push(<dd>{gettextSub('Created')}:&nbsp;{createdDT.toLocaleString()}</dd>);
            }

            articleLDJSON = getArticleLDJSON(this, gif);
            publisherLDJSON = articleLDJSON.publisher;
            gifLDJSON = articleLDJSON.image;
            videoLDJSON = articleLDJSON.video;
        }

        const style = {
            width: `${state.width}px`,
            height: `${state.height}px`,
        };
        const mediaStyle = {
            'max-width': `${state.width}px`,
            'min-height': state.minHeight,
        };
        const relatedGifs = this.props.relatedGifs;

        const gifQuality = state.gifQuality;
        const showVideo = gifQuality === MEDIA_QUALITY.VIDEO;
        // This controls what we expose in our markup to Google.

        const countryAllowed = gifCountryAllowed(gif, this.context);

        const allowCaptioning = countryAllowed && !this.isSticker;
        const allowFavoriting = countryAllowed && !this.isSticker;
        const showQualityToggle = countryAllowed;
        const showAuthor = countryAllowed;
        const showTags = gif?.tags.length > 0;
        const showSharingOptions = countryAllowed;
        const showGifDetails = true;
        const showRelatedGifs = relatedGifs.results.length > 0;

        const username = gif?.user?.username;
        const ownsGif = username &&
            this.context.authService.getUsername() === username;
        const showShareCount = process.env.BROWSER && ownsGif;

        return (
            <div className="GifPage container page">
                { gif && <Metadata page={this}>
                    {gif.geographic_restriction && <meta name="robots" content="noindex"/>}

                    <meta name="twitter:app:url:iphone" content={iosURL}/>
                    <meta name="twitter:app:url:ipad" content={iosURL}/>
                    <meta name="twitter:app:url:googleplay" content={androidURL}/>

                    <meta property="al:ios:url" content={iosURL} />
                    <meta property="al:android:url" content={androidURL} />

                    <link rel="alternate" href={`ios-app://917932200/riffsykeyboard/riff/${gif.id}`} />
                    <link rel="alternate" href={`android-app://com.riffsy.FBMGIFApp/riffsy/gifs/${gif.id}`} />

                    <meta name="twitter:image" content={gifFormat.url}/>
                    <meta name="twitter:card" content="player"/>
                    <meta name="twitter:player" content={`${this.embedUrl}?playertype=card`}/>
                    <meta name="twitter:player:width" content="498"/>
                    <meta name="twitter:player:height" content={Math.floor(498 / gifAspectRatio).toString()}/>
                    <meta name="twitter:player:stream" content={mp4Format.url}/>
                    <meta name="twitter:player:stream:content_type" content="video/mp4"/>

                    <link rel="alternate" type="application/json+oembed" title="GIF oEmbed Profile" href={this.fullURL(`/oembed?url=${this.encodedCanonicalURL}`)}/>
                    <link rel="alternate" type="text/xml+oembed" title="GIF oEmbed Profile" href={this.fullURL(`/oembed?url=${this.encodedCanonicalURL}&format=xml`)}/>

                    {gifOGMeta(gif, this.context)}

                    <script type="application/ld+json" dangerouslySetInnerHTML={{__html: safelySerializeJSON(articleLDJSON)}}/>
                </Metadata> }
                <div className="main-container">
                    <h1 style={{'margin-bottom': '30px'}}>{this.h1_title}</h1>
                    <div className="single-view-container" itemScope={true} itemType="http://schema.org/Article">
                        <meta itemProp="url" content={articleLDJSON.url}/>
                        <meta itemProp="mainEntityOfPage" content={articleLDJSON.mainEntityOfPage}/>
                        <meta itemProp="keywords" content={articleLDJSON.keywords}/>
                        <meta itemProp="dateModified" content={articleLDJSON.dateModified}/>
                        <meta itemProp="datePublished" content={articleLDJSON.datePublished}/>
                        <meta itemProp="author" content={articleLDJSON.author}/>
                        <meta itemProp="creator" content={articleLDJSON.creator}/>
                        <meta itemProp="headline" content={articleLDJSON.headline}/>
                        <meta itemProp="name" content={articleLDJSON.name}/>
                        <span className="hide" itemProp="publisher" itemScope={true} itemType="http://schema.org/Organization">
                            <meta itemProp="name"
                                content={publisherLDJSON.name}/>
                            <meta itemProp="logo"
                                content={publisherLDJSON.logo?.url}/>
                        </span>
                        {gif && <DetectContextMenuWrapper
                            callback={this.onGifContainerContextMenu}>
                            <div
                                id="single-gif-container"
                                ref={this.setGifContainer}
                            >
                                { !showVideo && countryAllowed && <div
                                    itemProp='image'
                                    itemScope={true}
                                    itemType='http://schema.org/ImageObject'
                                >
                                    <meta itemProp="keywords" content={gifLDJSON.keywords}/>
                                    <meta itemProp="dateCreated" content={gifLDJSON.dateCreated}/>
                                    <meta itemProp="uploadDate" content={gifLDJSON.uploadDate}/>
                                    <meta itemProp="author" content={gifLDJSON.author}/>
                                    <meta itemProp="creator" content={gifLDJSON.creator}/>
                                    <meta itemProp="embedUrl" content={gifLDJSON.embedUrl}/>
                                    <meta itemProp="representativeOfPage" content={gifLDJSON.representativeOfPage ? 'true' : 'false'}/>
                                    <meta itemProp="url" content={gifLDJSON.url}/>
                                    <meta itemProp="duration" content={articleLDJSON.video ? articleLDJSON.video.duration : ''}/>
                                    <meta itemProp="contentUrl" content={gifLDJSON.contentUrl}/>
                                    <meta itemProp="width" content={gifLDJSON.width}/>
                                    <meta itemProp="height" content={gifLDJSON.height}/>
                                    <Gif
                                        gif={gif}
                                        quality={gifQuality}
                                        width={state.width}
                                        height={state.height}
                                        style={style}
                                        mediaStyle={mediaStyle}
                                        onCompletedGif={this.onCompletedGif}
                                    />
                                </div>}

                                { showVideo && countryAllowed && <div
                                    itemProp='video'
                                    itemScope={true}
                                    itemType='http://schema.org/VideoObject'
                                >
                                    <meta itemProp="keywords" content={videoLDJSON.keywords}/>
                                    <meta itemProp="dateCreated" content={videoLDJSON.dateCreated}/>
                                    <meta itemProp="uploadDate" content={videoLDJSON.uploadDate}/>
                                    <meta itemProp="author" content={videoLDJSON.author}/>
                                    <meta itemProp="creator" content={videoLDJSON.creator}/>
                                    <meta itemProp="embedUrl" content={videoLDJSON.embedUrl}/>
                                    <meta itemProp="representativeOfPage" content={videoLDJSON.representativeOfPage ? 'true' : 'false'}/>
                                    <meta itemProp="url" content={videoLDJSON.url}/>
                                    <meta itemProp="duration" content={articleLDJSON.video ? articleLDJSON.video.duration : ''}/>
                                    <meta itemProp="contentUrl" content={videoLDJSON.contentUrl}/>
                                    <meta itemProp="width" content={videoLDJSON.width}/>
                                    <meta itemProp="height" content={videoLDJSON.height}/>

                                    { gif.hasaudio && <input
                                        className="mute-button"
                                        type="image"
                                        onClick={this.toggleMute}
                                        src={state.muted ? '/assets/img/icons/mute.svg' : '/assets/img/icons/sound.svg'}
                                        width="46"
                                        height="46"
                                    /> }
                                    <Gif
                                        gif={gif}
                                        quality={MEDIA_QUALITY.VIDEO}
                                        muted={state.muted}
                                        width={state.width}
                                        height={state.height}
                                        style={style}
                                        mediaStyle={mediaStyle}
                                    />
                                </div>}

                                {!countryAllowed && <div><GifRestrictedMessage
                                    width={state.width}
                                    height={state.height}
                                /></div> /*
                                    needs to be wrapped in a div to avoid
                                    rendering bug.
                                */}
                                {showShareCount && <ShareCountFooter
                                    gif={gif}
                                    height={state.height}
                                    width={state.width}
                                />}
                            </div>
                        </DetectContextMenuWrapper> }
                    </div>
                    { gif && <div className="gif-details-container">
                        <div className="gif-actions">
                            <div className="extra-controls">
                                { showQualityToggle &&
                                    <QualityToggle
                                        gifLoaded={state.gifLoaded}
                                        gifPage={this}
                                        gifQuality={gifQuality}
                                        gif={gif}
                                    />
                                }
                                { allowCaptioning && <CaptionButton
                                    gif={gif}
                                /> }

                                { allowFavoriting &&
                                    <GifFavButton gif={gif}/>
                                }
                            </div>
                            { showAuthor && username ? <div className="profile-info">
                                <ProfileImage
                                    user={gif.user}
                                    size="32"
                                    disableLinkToProfile={
                                        this.profileLinkingDisabled(gif)}
                                />
                                <Link
                                    className="author-username"
                                    rel="author"
                                    to={this.linkToProfile(gif.user)}
                                    disabled={this.profileLinkingDisabled(gif)}
                                >
                                    {gif.user.username}
                                </Link>
                                <OfficialBadge
                                    flags={gif.user.flags}
                                    tooltip={true}
                                />
                            </div> : <div className="profile-info"/>}
                        </div>

                        { (showSharingOptions || ownsGif) &&
                            <div className="gif-details share-buttons">
                                {showSharingOptions && <>
                                    {Object.entries(this.getShareButtons(
                                        gif)).map(([key, data]) => {
                                        return <ShareIcon
                                            key={key}
                                            service={key}
                                            title={this.title}
                                            onClick={this.onShareIconClick}
                                            {...data}
                                        />;
                                    })}
                                </>}
                                {ownsGif ?
                                    <TrashIcon gif={gif}/> :
                                    <FlagIcon/>
                                }
                            </div>
                        }

                        { showTags &&
                            <ul className="tag-list" style={{width: username ? '100%' : 'calc(100% - 200px)'}} >
                                {gif.tags.map((tag: string, i: number) => {
                                    tag = cleanMultiwordTag(tag);
                                    if (!tag) {
                                    // the cleaned tag might be an empty string.
                                        return;
                                    }
                                    return <li key={i}>
                                        <RelatedTag
                                            searchterm={tag}
                                            name={tag}
                                        />
                                    </li>;
                                })}
                            </ul>
                        }

                        { showSharingOptions &&
                            <div className="gif-details embed" style={{display: (!isMobile() || state.showCopyBox) ? 'block' : 'none'}}>
                                <h3>{gettextSub('Share URL')}</h3>
                                <Copybox
                                    className="share"
                                    onClick={this.registerShare}
                                    value={this.fullURL(this.linkToView(gif))}
                                />
                                <br />
                                <br />
                                <h3>{gettextSub('Embed')}</h3>
                                <Copybox
                                    className="embed"
                                    onClick={this.registerShare}
                                    value={gif.embed}
                                />
                            </div>
                        }

                        { showGifDetails &&
                            <div className="gif-details non-mobile-only">
                                <h3>{gettextSub('Details')}</h3>
                                <dl>
                                    { detailsEls }
                                </dl>
                            </div>
                        }
                    </div> }
                </div>

                { showRelatedGifs &&
                    <RelatedGifsContainer
                        currentGifId={gif?.id}
                        relatedGifs={relatedGifs}
                    />
                }

                <UpsellPill
                    text={gettextSub('Share from GIF Keyboard')}
                    to="https://apps.apple.com/app/apple-store/id917932200?pt=39040802&ct=viewpagev2&mt=8"
                    origin='itemview'
                />
            </div>
        );
    }
}
